import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/Command';
import { useUi } from '@/store/ui';
import { useTheme } from '@/store/theme';
import { navForRole, quickActionsForRole } from '@/routes/nav';
import { useAuth } from '@/store/auth';
import { Moon, Sun } from 'lucide-react';

export function CommandPalette() {
  const open = useUi((s) => s.commandOpen);
  const setOpen = useUi((s) => s.setCommandOpen);
  const navigate = useNavigate();
  const role = useAuth((s) => s.profile?.role);
  const themeToggle = useTheme((s) => s.toggle);
  const theme = useTheme((s) => s.theme);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  const groupedNav = useMemo(() => {
    const items = navForRole(role);
    const map = new Map<string, typeof items>();
    for (const item of items) {
      const g = item.group ?? 'Navigate';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    }
    return Array.from(map.entries());
  }, [role]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, actions…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {groupedNav.map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.to}
                  onSelect={() => go(item.to)}
                  value={`${group} ${item.label}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
        <CommandSeparator />
        <CommandGroup heading="Quick actions">
          {quickActionsForRole(role).map((q) => (
            <CommandItem key={q.to} onSelect={() => go(q.to)} value={`action ${q.label}`}>
              <span>{q.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Preferences">
          <CommandItem
            onSelect={() => {
              themeToggle();
              setOpen(false);
            }}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>Switch to {theme === 'dark' ? 'light' : 'dark'} mode</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <CommandFooter>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded border border-app-border bg-app-surface px-1.5 py-0.5 text-[10px] font-medium">↑↓</kbd>
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded border border-app-border bg-app-surface px-1.5 py-0.5 text-[10px] font-medium">↵</kbd>
            select
          </span>
        </div>
        <span className="inline-flex items-center gap-1">
          <kbd className="rounded border border-app-border bg-app-surface px-1.5 py-0.5 text-[10px] font-medium">⌘K</kbd>
          to open
        </span>
      </CommandFooter>
    </CommandDialog>
  );
}
